/**
 * No-op storage adapter for graceful degradation
 * Used when both IndexedDB and localStorage are unavailable
 * 
 * This adapter:
 * - Implements all EventQueue methods as no-ops
 * - Logs warnings when used to alert developers
 * - Ensures the tracking SDK continues to function without storage
 * 
 * Requirements: 1.8, 14.1
 */

import { EventQueue } from '../core/EventQueue';
import { TrackingEvent } from '../types/events';

export class NoOpAdapter implements EventQueue {
  private hasWarned = false;

  /**
   * Log a warning message once when the no-op adapter is used
   */
  private logWarning(method: string): void {
    if (!this.hasWarned) {
      console.warn(
        `[Tracking SDK] No persistent storage available. Events will not be queued. ` +
        `Method '${method}' called on NoOpAdapter - no action taken.`
      );
      this.hasWarned = true;
    }
  }

  /**
   * No-op implementation: Event is not stored
   */
  async enqueue(_event: TrackingEvent): Promise<void> {
    this.logWarning('enqueue');
    // No-op: event is not stored
  }

  /**
   * No-op implementation: Returns empty array
   */
  async dequeue(_count: number): Promise<TrackingEvent[]> {
    this.logWarning('dequeue');
    return [];
  }

  /**
   * No-op implementation: Returns empty array
   */
  async peek(_count: number): Promise<TrackingEvent[]> {
    this.logWarning('peek');
    return [];
  }

  /**
   * No-op implementation: Nothing to remove
   */
  async remove(_eventIds: string[]): Promise<void> {
    this.logWarning('remove');
    // No-op: nothing to remove
  }

  /**
   * No-op implementation: Always returns 0
   */
  async size(): Promise<number> {
    this.logWarning('size');
    return 0;
  }

  /**
   * No-op implementation: Nothing to clear
   */
  async clear(): Promise<void> {
    this.logWarning('clear');
    // No-op: nothing to clear
  }
}
